import { memo, useEffect, useState } from 'react';
import {
  fetchAuthSession,
  fetchUserAttributes,
  signOut,
} from 'aws-amplify/auth';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Session from 'api/storage/session';
import UserActivityTime from 'api/storage/userActivityTime';
import LastUrlStorage from 'api/storage/lastUrl';
import { useAutofocus } from 'hooks/useAutofocus';
import { errorToast } from 'helpers/toast';
import { Params, FormSteps } from '../../types';
import { getStepName } from '../../helpers';
import { messages } from '../../messages';
import { FormPanelInnerContainer } from '../FormContainer/styled';
import { UsernameStep } from '../UsernameStep';
import { MFAStep } from '../MFAStep';
import { PasswordStep } from '../PasswordStep';
import { VerifyAccountStep } from '../VerifyAccountStep';
import { PasswordResetEmailStep } from '../PasswordResetEmailStep';
import { PasswordResetSuccessScreen } from '../PasswordResetSuccessScreen';
import { PasswordResetCodeStep } from '../PasswordResetCodeStep';
import { SetUpPasswordStep } from '../SetUpPasswordStep';

interface Props {
  route: 'login' | 'register';
}

export const LoginForm = memo(({ route }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const { redirectUri, forwardedState } = (location.state || {}) as Params;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [smsDestination, setSmsDestination] = useState<string | undefined>(
    undefined,
  );
  const [currentStep, setCurrentStep] = useState<FormSteps>(
    route === 'register'
      ? FormSteps.SET_UP_PASSWORD_STEP
      : FormSteps.USERNAME_STEP,
  );
  const autofocus = useAutofocus(currentStep !== FormSteps.PASSWORD_STEP, [
    currentStep,
  ]);

  // to handle back/forward buttons
  useEffect(() => {
    if (route === 'register') {
      setCurrentStep(FormSteps.SET_UP_PASSWORD_STEP);
    } else setCurrentStep(FormSteps.USERNAME_STEP);
  }, [route]);

  const onLoginSuccess = async () => {
    try {
      const attributes = await fetchUserAttributes();
      const userId = attributes['custom:userId'] as string;
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();

      // A signed-in session must have an id token; bail out loudly rather than
      // navigating into an authenticated area without a usable token (which
      // would otherwise trigger an immediate logout on the first API call).
      if (!idToken) throw new Error('Missing id token after sign-in');

      UserActivityTime.saveUserTime(new Date(), userId);
      Session.saveSession(idToken);

      navigate(LastUrlStorage.load(userId) ?? redirectUri ?? '/registries', {
        state: forwardedState,
      });
      LastUrlStorage.clear();
    } catch {
      // Amplify reported a signed-in session but we couldn't establish the
      // app session (missing token / attributes). Reset to a clean state so a
      // retry isn't blocked by UserAlreadyAuthenticatedException.
      await signOut().catch(() => undefined);
      errorToast(t(messages.errorOccurredTryAgain()), {
        position: 'top-right',
      });
    }
  };

  const getStepForm = () => {
    switch (currentStep) {
      case FormSteps.USERNAME_STEP:
        return (
          <UsernameStep
            onUsernameSet={newUsername => {
              setUsername(newUsername);
              setCurrentStep(FormSteps.PASSWORD_STEP);
            }}
            onNotConfirmed={newUsername => {
              setUsername(newUsername);
              setCurrentStep(FormSteps.VERIFY_ACCOUNT);
            }}
          />
        );
      case FormSteps.PASSWORD_STEP:
        return (
          <PasswordStep
            onSuccess={onLoginSuccess}
            onMFA={(destination, pass) => {
              setCurrentStep(FormSteps.VALIDATION_STEP);
              setSmsDestination(destination);
              setPassword(pass);
            }}
            onNotConfirmed={pass => {
              setPassword(pass);
              setCurrentStep(FormSteps.VERIFY_ACCOUNT);
            }}
            onForgotPassword={() => {
              setCurrentStep(FormSteps.RESET_PASSWORD_EMAIL_STEP);
            }}
            onBack={() => {
              setCurrentStep(FormSteps.USERNAME_STEP);
            }}
            username={username}
          />
        );
      case FormSteps.VERIFY_ACCOUNT:
        return (
          <VerifyAccountStep
            username={username}
            onReturn={() => setCurrentStep(FormSteps.PASSWORD_STEP)}
            onLogin={() => {
              navigate({ pathname: `/login`, search });
              setCurrentStep(FormSteps.USERNAME_STEP);
            }}
          />
        );
      case FormSteps.VALIDATION_STEP:
        return (
          <MFAStep
            onSuccess={onLoginSuccess}
            smsDestination={smsDestination}
            username={username}
            password={password}
            onExpire={() => {
              setSmsDestination(undefined);
              setUsername('');
              setPassword('');
              setCurrentStep(FormSteps.USERNAME_STEP);
            }}
          />
        );
      case FormSteps.RESET_PASSWORD_EMAIL_STEP:
        return (
          <PasswordResetEmailStep
            onSuccess={name => {
              setUsername(name);
              setCurrentStep(FormSteps.RESET_PASSWORD_CODE_STEP);
            }}
            onBack={() => {
              setCurrentStep(FormSteps.PASSWORD_STEP);
            }}
            username={username}
          />
        );
      case FormSteps.RESET_PASSWORD_CODE_STEP:
        return (
          <PasswordResetCodeStep
            onSuccess={() => {
              setCurrentStep(FormSteps.RESET_PASSWORD_SUCCESS_SCREEN);
            }}
            onBack={() => {
              setCurrentStep(FormSteps.RESET_PASSWORD_EMAIL_STEP);
            }}
            onReturn={() => {
              setSmsDestination(undefined);
              setPassword('');
              setCurrentStep(FormSteps.PASSWORD_STEP);
            }}
            username={username}
          />
        );
      case FormSteps.RESET_PASSWORD_SUCCESS_SCREEN:
        return (
          <PasswordResetSuccessScreen
            onReturn={() => setCurrentStep(FormSteps.USERNAME_STEP)}
          />
        );
      case FormSteps.SET_UP_PASSWORD_STEP:
        return (
          <SetUpPasswordStep
            onLogin={() => {
              navigate({ pathname: `/login`, search });
              setCurrentStep(FormSteps.USERNAME_STEP);
            }}
            onAutologin={onLoginSuccess}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormPanelInnerContainer {...autofocus}>
      <Helmet>
        <title>
          {t(messages.loginPage())} - {getStepName(currentStep)}
        </title>
      </Helmet>
      {getStepForm()}
    </FormPanelInnerContainer>
  );
});
